// src/services/moderation.js — Admin → Moderation data (member replies).
// adr-member-replies-threads-and-moderation.md §3; UX member-replies.md §6.
//
// Reads are admin-only (rules: moderationQueue read requires role == admin).
// All decisions go through /api/moderate-comment (server owns every state
// transition and the comment count).

import { db, firebaseEnabled } from './firebase';
import { collection, query, where, onSnapshot, doc, getDoc } from 'firebase/firestore';

const HANDLED_DAYS = 30;

// "Needs you": everything awaiting Danielle's decision, oldest first.
export function subscribeNeedsReview(onUpdate) {
    if (!firebaseEnabled || !db) { onUpdate([]); return () => {}; }
    const q = query(collection(db, 'moderationQueue'), where('state', '==', 'needs_review'));
    return onSnapshot(q, (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }))
            .sort((a, b) => String(a.createdAt).localeCompare(String(b.createdAt)));
        onUpdate(rows);
    }, (err) => { console.error('subscribeNeedsReview:', err); onUpdate([]); });
}

// "Handled for you": what the AI hid on its own in the last 30 days. It
// "clears itself" by this filter — nothing is deleted.
export function subscribeHandled(onUpdate) {
    if (!firebaseEnabled || !db) { onUpdate([]); return () => {}; }
    const q = query(collection(db, 'moderationQueue'), where('state', '==', 'auto_removed'));
    return onSnapshot(q, (snap) => {
        const cutoff = new Date(Date.now() - HANDLED_DAYS * 86400000).toISOString();
        onUpdate(snap.docs.map((d) => ({ id: d.id, ...d.data() }))
            .filter((r) => String(r.createdAt) >= cutoff)
            .sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt))));
    }, (err) => { console.error('subscribeHandled:', err); onUpdate([]); });
}

export async function moderateComment(commentId, action, getToken) {
    try {
        const token = await getToken();
        const r = await fetch('/api/moderate-comment', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ commentId, action }),
            keepalive: true,
        });
        return r.ok;
    } catch (e) { return false; }
}

// Small lookups for card context (class title, the comment being replied to).
const cache = new Map();
export async function lookupDoc(col, id) {
    if (!id || !firebaseEnabled || !db) return null;
    const key = `${col}/${id}`;
    if (cache.has(key)) return cache.get(key);
    const p = getDoc(doc(db, col, id)).then((s) => (s.exists() ? s.data() : null)).catch(() => null);
    cache.set(key, p);
    return p;
}
